import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { top } from '../utils/customPropStyles';
import Badge from './Badge';
import Chevron from './Chevron';
import Translate, { translate } from './Translate';
import FilterSearch from './FilterSearch';
import FilterValue from './FilterValue';
import ClickLayer from './ClickLayer';
import { FilterStore } from '../stores';
import { FilterSearchKeys } from '../stores/FilterStore';

const FilterDropdownWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterDropdownWrapper.defaultProps = {
    flex: ["0 0 100%", "0 0 33%", "0 0 20%"],
    maxWidth: ["100%", "33%", "20%"],
};

const FilterDropdownInnerWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FilterDropdownInnerWrapper.defaultProps = {
    position: "relative",
    marginX: "lg",
    marginY: "sm"
};

const FilterDropdownHeader = styled<any, any>('h4')`
    ${propStyles};
`;

FilterDropdownHeader.defaultProps = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "sm",
    fontFamily: "heading",
    fontWeight: "medium",
    color: "body",
    margin: "0 0 0.5em 0"
};

const FilterDropdownButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

FilterDropdownButton.defaultProps = {
    display: "flex",
    flex: "0 0 auto",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "sm",
    fontSize: "sm",
    fontFamily: "body",
    fontWeight: "normal",
    color: "gray500",
    bgColor: "white",
    borderWidth: "1px",
    borderColor: "gray300",
    borderStyle: "solid",
    borderRadius: "sm",
    hover: {
        borderColor: "primary"
    },
    selected: {
        color: "body",
        borderColor: "primary"
    }
};

const FilterDropdownText = styled<any, any>('div')`
    ${propStyles};
    white-space: nowrap;
`;

FilterDropdownText.defaultProps = {
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "inherit"
};

const FilterDropdown = styled<any, any>('div')`
    ${propStyles};
    ${top};
`;

FilterDropdown.defaultProps = {
    position: "absolute",
    top: "calc(100% + 1px)",
    left: "0",
    right: "0",
    zIndex: "dropdown",
    padding: "md",
    bgColor: "white",
    borderWidth: "1px",
    borderColor: "gray300",
    borderStyle: "solid",
    borderRadius: "sm",
    boxShadow: "sm"
};

const FilterList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

FilterList.defaultProps = {
    maxHeight: "300px",
    overflowX: "hidden",
    overflowY: "auto",
};

const Divider = styled<any, any>('hr')`
    ${propStyles};
` as any; // TODO: why?!

Divider.defaultProps = {
    borderWidth: "1px",
    borderColor: "gray500",
    borderStyle: "dotted none none none"
};

type Filter = {
    type: 'CLSS' | 'DOCUMENT_META' | 'DOCUMENT_PROPERTY';
    property: string;
    label: string;
    values: Array<FilterValue>;
    ui?: string;
    expanded?: boolean;
    enableSearch?: boolean;
    filterSearchKey: FilterSearchKeys;
};

type FilterValue = {
    label: string;
    value: string;
};

interface FilterItemProps {
    theme?: { [property: string]: string | number };
    translations?: { [property: string]: string };
    filterStore?: FilterStore;
    filter: Filter;
    expanded: boolean;
    toggleExpanded: Function;
    selectedCount: number;
    total: number; 
    search: string;
    clearSearch: Function;
    selectedValues: Array<FilterValue>;
    filteredValues: Array<FilterValue>;
    filterSearch: Function; 
    enableSearch: boolean;
    filterDropdownText: string;
}

@inject('translations')
@inject('filterStore')
@observer
class FilterItemDropdown extends Component<FilterItemProps> {
    render() {
        const { 
            theme, 
            translations, 
            filterStore, 
            filter,

            // withFilter HoC props
            expanded,
            toggleExpanded, 
            selectedCount, 
            total, 
            search, 
            clearSearch, 
            selectedValues, 
            filteredValues,
            filterSearch, 
            enableSearch,
            filterDropdownText
        } = this.props;

        return (
            <FilterDropdownWrapper
                {...theme.FilterDropdownWrapper}
            >
                <FilterDropdownInnerWrapper
                    {...theme.FilterDropdownInnerWrapper}
                >
                    <FilterDropdownHeader
                        {...theme.FilterDropdownHeader}
                    >
                        <Translate>{filter.label}</Translate>
                        {
                            selectedCount > 0 && (
                                <Badge
                                    fontSize="0.75em"
                                    data-selected={selectedCount > 0}
                                    selected={{
                                        bgColor: "primary"
                                    }}
                                    {...theme.FilterDropdownTotalBadge}
                                >
                                    {selectedCount}/{total}
                                </Badge>
                            )
                        }
                    </FilterDropdownHeader>
                    <FilterDropdownButton
                        onClick={toggleExpanded}
                        title={filterDropdownText}
                        data-selected={expanded || selectedCount > 0}
                        {...theme.FilterDropdownButton}
                    >
                        <FilterDropdownText
                            {...theme.FilterDropdownText}
                        >
                            {
                                filterDropdownText
                            }
                        </FilterDropdownText>
                        <Chevron
                            direction={expanded ? 'down' : 'right'}
                        />
                    </FilterDropdownButton>
                    {
                        expanded && (
                            <Fragment>
                                <ClickLayer
                                    onClick={toggleExpanded}
                                />
                                <FilterDropdown
                                    {...theme.FilterDropdown}
                                >
                                    {
                                        enableSearch && (
                                            <FilterSearch
                                                type="text"
                                                placeholder={
                                                    `${translate(translations, "Search")} ${translate(translations, filter.label)}`}
                                                value={search}
                                                onChange={e => filterSearch(e.target.value)}
                                                onClear={clearSearch}
                                                showClear={search.length > 0}
                                                {...theme.FilterDropdownSearch}
                                            />
                                        )
                                    }
                                    <FilterList
                                        {...theme.FilterDropdownSelectedFilterList}
                                    >
                                        {
                                            selectedValues.map((value, valueIndex) =>
                                                <FilterValue
                                                    key={`${value.label}-${valueIndex}`}
                                                    label={value.label}
                                                    checked={true}
                                                    onChange={() => 
                                                        filterStore.toggleFilter(filter.property, value.value, filter.filterSearchKey, true)}
                                                    active={true}
                                                    {...theme.FilterDropdownSelectedFilterListItem}
                                                />
                                            )
                                        }
                                    </FilterList>
                                    {
                                        (selectedValues.length > 0 && filteredValues.length > 0) &&
                                        <Divider
                                            {...theme.FilterDropdownFilterListDivider}
                                        />
                                    }
                                    <FilterList
                                        {...theme.FilterDropdownFilterList}
                                    >
                                        {
                                            filteredValues.map((value, valueIndex) =>
                                                <FilterValue
                                                    key={`${value.label}-${valueIndex}`}
                                                    label={value.label}
                                                    checked={false}
                                                    onChange={() => 
                                                        filterStore.toggleFilter(filter.property, value.value, filter.filterSearchKey, false)}
                                                    active={filterStore.isActive(filter.property, value.value)}
                                                    {...theme.FilterDropdownFilterListItem}
                                                />
                                            )
                                        }
                                    </FilterList>
                                </FilterDropdown>
                            </Fragment>
                        )
                    }
                </FilterDropdownInnerWrapper>
            </FilterDropdownWrapper>
        );
    }
}

export default withTheme(FilterItemDropdown);


// WEBPACK FOOTER //
// ./src/Components/FilterItemDropdown.tsx